import React, { useEffect, useRef, useState } from "react";
import Image from "react-bootstrap/Image";
import LoginLeftBg from "../../assets/img/login-left-bg.jpg";
import KshemaLogo from "../../assets/img/kshema_logo.svg";
import "./LoginPage.css";
import { Button, Col, Container, Form, Label, Row } from "reactstrap";
import CenterSpinner from "../../components/CenterSpinner";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import {
  setAuthenticated,
  setLocalLogout,
} from "../../redux/slices/loginSlice";
import { LOGIN_STATUS, STORAGE_KEY } from "../../utils/Constant";
import { toast } from "react-toastify";
import { UserManager } from "oidc-client-ts";
import oidcConfig, { userManager } from "../../utils/AuthConfig";
import { getRoleId, getRolePermission } from "../../utils/RBAC";
import { assignUserToGroup, getAdminTokenApi, getChildGroups, getGroupByName, getUserGroups, invalidateExistingSessions, refreshAuthToken } from "../../utils/AuthUtil";
import { saveInLocalStorage } from "../../utils/Util";

interface ILoginProps {}

const LoginPage: React.FunctionComponent<ILoginProps> = (
  props: ILoginProps
) => {
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  const checkAuthentication = async () => {
    try {
      const user = await userManager.getUser();
      if (user && user.access_token) {
        console.log("User already authenticated:", user);
        onSuccessfulLogin(user);
      } else {
        console.log("User not authenticated, redirecting to login...");
        callLogin();
      }
    } catch (error) {
      console.error("Error checking authentication:", error);
      callLogin();
    }
  };
 
  const callLogin = () => {
    userManager.signinRedirect().then(response => {
      console.log('Login initiated', response);    
    }).catch((error) => {
      console.error('Login error:', error);
    });
  };
 
  const handleCallback = async () => {
    try {
      const user = await userManager.signinRedirectCallback();
      if (user) {
        console.log("Callback successful, user authenticated:", user);
        setShowLoader(true)
        onSuccessfulLogin(user);
      }
    } catch (error) {
      console.error("Callback processing error:", error);
    }
  };
 
  useEffect(() => {
    const isCallbackUrl = window.location.href.includes("code=");
    
    if (isCallbackUrl) {
      // Only process the callback if the user was redirected from login
      console.log("Processing authentication callback...");
      handleCallback();
    } else {
      // Check authentication status before redirecting to login
      setTimeout(() => {
        checkAuthentication();
      },200)
    }
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setShowLoader(false)
    },2000)
  },[showLoader === true])

  
  const onSuccessfulLogin = async (payload: any) => {
    const AT = payload.accessToken || payload.access_token;
    const tokenPayload = JSON.parse(atob(AT.split('.')[1]));
  
    const data = {
      employeeName: payload.profile.name,
      email: payload.profile.email,
      accessToken: payload.access_token,
      accessExpiryTime: payload.expires_at,
      resetToken: payload.refresh_token,
      resetExpiryTime: payload.expires_at,
      mobile_device_token: null,
      permissions: getRolePermission(tokenPayload),
      roleId: getRoleId(tokenPayload)?.roleId,
      roleName: getRoleId(tokenPayload)?.roleName,
      userId: tokenPayload?.sub,
      sessionId: payload.session_state,
    };
    
    saveInLocalStorage(
      STORAGE_KEY.AUTH_DATA,
      JSON.stringify(data)
    ); 
    let adminToken = await getAdminToken();
  
    const userGroups = await getUserGroups(data.userId, adminToken);

    const isInEmployeeGroup = userGroups.some((group: any) => group.name === 'Employee Group');
    const isInGrievanceGroup = userGroups.some((group: any) => group.name === 'Grievance');

    if (isInEmployeeGroup && !isInGrievanceGroup) {
      const employeeGroup = userGroups.find((group: any) => group.name === 'Employee Group');
      if (employeeGroup) {
        const childGroups = await getChildGroups(employeeGroup.id, adminToken);
        const grievanceGroup = childGroups.find((group: any) => group.name === 'Grievance');
        if (grievanceGroup) {
          await assignUserToGroup(data.userId, grievanceGroup.id, adminToken);
          let newAccessToken = await refreshAuthToken(data.resetToken, dispatch);
          const tokenPayload = JSON.parse(atob(newAccessToken.split('.')[1]));
          await invalidateExistingSessions(tokenPayload?.sub, adminToken, tokenPayload?.sid);
        }
      }
    } else if (isInEmployeeGroup && isInGrievanceGroup) {
      await invalidateExistingSessions(data?.userId, adminToken, payload?.session_state);
      toast.success('Logged in successfully');
      dispatch(
        setAuthenticated({
          status: LOGIN_STATUS.AUTHENTICATED,
          user: data,
        })
      );
    }
  }

  const getAdminToken = async () => {
    let token: any;
    await getAdminTokenApi().then((adminToken: any) => {
      token = adminToken.token;
    });
    return token;
  };

  return (
    <div>
      <CenterSpinner showLoader={showLoader} />
    </div>
  );
};

export default LoginPage;
